<!-- Hero Section with Search -->
<app-job-hero (openAdvancedSearch)="openModal()" (search)="onSearch($event)" (clearLocation)="clearAllFilters()">
  <span filter-count class="filter-count" *ngIf="activeFilters.length > 0">{{ activeFilters.length }}</span>

  <!-- Project active filters into the hero component -->
  <app-active-filters
    active-filters
    [filters]="activeFilters"
    [isOpen]="activeFiltersOpen"
    (removeFilter)="removeFilter($event)"
    (clearAllFilters)="clearAllFilters()"
    (toggleOpen)="toggleActiveFilters()"></app-active-filters>
</app-job-hero>

<!-- AI Recommended Jobs CTA Banner -->
<section class="ai-cta" *ngIf="isLoggedIn && !aiCtaDismissed">
  <div class="container">
    <div class="ai-cta__inner">
      <div class="ai-cta__inner-wrap">
        <div class="ai-cta__icon-wrap">
          <i class="fas fa-robot"></i>
          <span class="ai-cta__pulse"></span>
        </div>
        <div class="ai-cta__content">
          <p class="ai-cta__label">{{ 'pages.jobs.aiCta.label' | transloco }}</p>
          <h2 class="ai-cta__title">{{ 'pages.jobs.aiCta.title' | transloco }}</h2>
          <p class="ai-cta__text">{{ 'pages.jobs.aiCta.text' | transloco }}</p>
        </div>
      </div>
      <a [href]="getLocalizedRoute('/jobs?tab=recommended')" class="ai-cta__btn">
        {{ 'pages.jobs.aiCta.btn' | transloco }}
        <i class="fas fa-arrow-right"></i>
      </a>
    </div>
    <button
      type="button"
      class="ai-cta__close"
      (click)="dismissAiCta()"
      [attr.aria-label]="'common.close' | transloco">
      <i class="fas fa-times" aria-hidden="true"></i>
    </button>
  </div>
</section>

<!-- Job Listings Section -->
<section class="job-listings">
  <div class="container">
    <div class="job-listings__wrapper">
      <!-- Sidebar Filters -->
      <!-- Mobile Filter Toggle Button -->
      <div class="side-filters">
        <div class="mobile-filter-toggle">
          <button class="mobile-filter-btn" (click)="toggleMobileFilters()">
            <i class="fas" [class.fa-filter]="!mobileFiltersOpen" [class.fa-times]="mobileFiltersOpen"></i>
            <span>{{ (mobileFiltersOpen ? 'filters.hideFilters' : 'filters.showFilters') | transloco }}</span>
            <span class="filter-badge" *ngIf="activeFilters.length > 0 && !mobileFiltersOpen">{{ activeFilters.length }}</span>
          </button>
          <button class="mobile-clear-btn" *ngIf="activeFilters.length > 0" (click)="clearAllFilters()">
            <i class="fas fa-times"></i>
            <span>{{ 'filter.clear' | transloco }}</span>
          </button>
        </div>
        <app-filter-sidebar [class.mobile-open]="mobileFiltersOpen" (closeMobile)="toggleMobileFilters()" (clearAll)="clearAllFilters()"></app-filter-sidebar>
      </div>

      <!-- Job Listings -->
      <div class="jobs">
        <div class="jobs__header">
          <div class="jobs__count">{{ totalJobs }} {{ 'jobs.found' | transloco }}</div>
          <div class="jobs__sort">
            <span>{{ 'jobs.sortBy' | transloco }}:</span>
            <div class="dropdown">
              <button class="dropdown__btn" (click)="sortDropdownOpen = !sortDropdownOpen; $event.stopPropagation()">
                {{ getSortLabel(sortBy) | transloco }}
                <i class="fas fa-chevron-down"></i>
              </button>
              <div class="dropdown__menu" [style.display]="sortDropdownOpen ? 'block' : 'none'">
                <div
                  class="dropdown__item"
                  *ngFor="let option of sortOptions"
                  [class.active]="option.value === sortBy"
                  (click)="setSortBy(option.value)">
                  {{ option.label | transloco }}
                  <i *ngIf="option.value === sortBy" class="fas fa-check dropdown__item-check" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <app-skeleton-loader *ngIf="loading && jobs.length === 0" type="card" [count]="3"></app-skeleton-loader>

        <!-- Error state -->
        <div class="jobs__error" *ngIf="error && !loading">
          <app-error [title]="'jobs.error.title' | transloco" [message]="'jobs.error.message' | transloco" [showRetry]="true" (retry)="loadJobs()"></app-error>
        </div>

        <!-- Empty state -->
        <div class="jobs__empty" *ngIf="!loading && !error && jobs.length === 0">
          <i class="fas fa-search"></i>
          <p>{{ 'jobs.noResults' | transloco }}</p>
          <button class="btn btn--primary" (click)="clearAllFilters()">{{ 'jobs.clearFilters' | transloco }}</button>
        </div>

        <!-- Job listings -->
        <div class="jobs__list" *ngIf="!loading && !error && jobs.length > 0">
          <app-job-detail-card
            *ngFor="let job of mappedJobs"
            [job]="job"
            [mode]="'browse'"
            [isLoggedIn]="isLoggedIn"
            [jobDetailRoute]="getLocalizedRoute('/job/' + job.id)"
            (saveJob)="onSaveJob($event)"
            (unsaveJob)="onUnsaveJob($event)"></app-job-detail-card>
        </div>

        <!-- Pagination — minimal: one row with page numbers + chevron arrows
             on the left, a small native select for page size on the right. -->
        <div class="pagination" *ngIf="totalPages > 0">
          <nav class="pagination__nav" [attr.aria-label]="'pagination.label' | transloco">
            <button
              type="button"
              class="pagination__arrow"
              [disabled]="currentPage === 1"
              (click)="prevPage()"
              [attr.aria-label]="'pagination.previous' | transloco">
              <i class="fas fa-chevron-left"></i>
            </button>

            <ng-container *ngFor="let page of getVisiblePages()">
              <span class="pagination__ellipsis" *ngIf="page === 'ellipsis'">…</span>
              <button
                type="button"
                *ngIf="page !== 'ellipsis'"
                class="pagination__page"
                [class.is-active]="currentPage === page"
                [attr.aria-current]="currentPage === page ? 'page' : null"
                (click)="goToPage($any(page))">
                {{ page }}
              </button>
            </ng-container>

            <button
              type="button"
              class="pagination__arrow"
              [disabled]="currentPage === totalPages"
              (click)="nextPage()"
              [attr.aria-label]="'pagination.next' | transloco">
              <i class="fas fa-chevron-right"></i>
            </button>
          </nav>

          <div class="pagination__size">
            <label for="page-size-select">{{ 'pagination.show' | transloco }}:</label>
            <div class="pagination__size-wrap">
              <select
                id="page-size-select"
                class="pagination__size-select"
                [value]="pageSize"
                (change)="setPageSize(+$any($event.target).value)">
                <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
              </select>
              <i class="fas fa-chevron-down pagination__size-chevron" aria-hidden="true"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Advanced Filter Modal -->
<div class="modal" [class.active]="isModalOpen" (click)="closeModal()">
  <div class="modal__overlay">
    <div class="modal__content" (click)="stopPropagation($event)">
      <div class="modal__header">
        <h2>{{ 'filter.advanced' | transloco }}</h2>
        <button class="modal__close" (click)="closeModal()">&times;</button>
      </div>
      <div class="modal__body">
        <!-- Salary Range -->
        <div class="advanced-filter__section">
          <h3>{{ 'filter.salaryRange' | transloco }}</h3>
          <div class="advanced-filter__salary">
            <div class="salary-inputs">
              <div class="salary-input">
                <span>{{ 'filter.min' | transloco }}</span>
                <div class="input-with-icon">
                  <input type="number" [(ngModel)]="salaryRange.min" placeholder="0" min="0" />
                  <span class="currency">€</span>
                </div>
              </div>
              <div class="salary-input">
                <span>{{ 'filter.max' | transloco }}</span>
                <div class="input-with-icon">
                  <input type="number" [(ngModel)]="salaryRange.max" placeholder="0" min="0" />
                  <span class="currency">€</span>
                </div>
              </div>
            </div>
            <div class="salary-period">
              <label class="radio" *ngFor="let period of salaryPeriods">
                <input type="radio" name="salary-period" [value]="period.periodKey" [(ngModel)]="selectedSalaryPeriod" />
                <span class="radio__label">{{ 'jobSalaryPeriods.' + period.periodKey | transloco }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Employment Type -->
        <div class="advanced-filter__section">
          <h3>{{ 'filters.employmentType' | transloco }}</h3>
          <div class="advanced-filter__checkboxes">
            <label class="checkbox" *ngFor="let empType of employmentTypes">
              <input type="checkbox" [(ngModel)]="empType.checked" />
              <span class="checkbox__label">
                {{ 'jobEmploymentTypes.' + empType.key | transloco }}
                <span class="count">({{ empType.count }})</span>
              </span>
            </label>
          </div>
        </div>

        <!-- Education Level -->
        <div class="advanced-filter__section">
          <h3>{{ 'filters.requiredEducation' | transloco }}</h3>
          <div class="advanced-filter__checkboxes">
            <label class="checkbox" *ngFor="let eduLevel of educationLevels">
              <input type="checkbox" [(ngModel)]="eduLevel.checked" />
              <span class="checkbox__label">
                {{ 'jobEducationLevels.' + eduLevel.key | transloco }}
                <span class="count">({{ eduLevel.count }})</span>
              </span>
            </label>
          </div>
        </div>
      </div>
      <div class="modal__footer">
        <div style="display: flex; gap: 0.5rem">
          <button class="btn btn--text" (click)="closeModal()">{{ 'button.cancel' | transloco }}</button>
          <button class="btn btn--text" (click)="applyAdvancedFilters()">{{ 'filter.apply' | transloco }}</button>
        </div>
        <button class="btn clear-btn" [class.active]="hasActiveFiltersInModal()" (click)="clearAllFilters(); closeModal()">
          {{ 'filter.clear' | transloco }}
        </button>
      </div>
    </div>
  </div>
</div>
